import { useEffect, useState } from "react";
import styles from "./issaInsights.module.scss";
import { useAssistant } from "../../config/assistantProfile.ts";
import { AssistantAvatar } from "../../components/ui/assistantAvatar.tsx";
import { IssaAnimatedAvatar, type IssaAvatarState } from "./issaAnimatedAvatar.tsx";
import { MaiaAnimatedAvatar } from "./maiaAnimatedAvatar.tsx";
import type { IssaActivity } from "./issaActivity.ts";

/** Tints the figure's floor glow by the conversation's model. A brand's own
 *  assistant model stands in for the Analyst one. */
export function avatarModelClass(modelConfigId?: string, assistantModelId?: string | null): string {
  const id = modelConfigId ?? "auto";
  if (assistantModelId && id === assistantModelId) return styles.avatarAiAnalyst ?? "";
  if (id === "assist") return styles.avatarAiGuide ?? "";
  if (id === "default") return styles.avatarAiAnalyst ?? "";
  return styles.avatarAiAuto ?? "";
}

/** A four-pointed sparkle centred on (x, y). */
function sparkle(x: number, y: number, r: number): string {
  return `M${x} ${y - r}Q${x} ${y} ${x + r} ${y}Q${x} ${y} ${x} ${y + r}Q${x} ${y} ${x - r} ${y}Q${x} ${y} ${x} ${y - r}Z`;
}

function ThoughtIcon({ activity }: { activity: IssaActivity }) {
  if (activity === "data") {
    return (
      <g className={styles.thoughtChart}>
        <rect x="50" y="23" width="6" height="8" rx="1" />
        <rect x="58" y="17" width="6" height="14" rx="1" />
        <rect x="66" y="12" width="6" height="19" rx="1" />
      </g>
    );
  }
  if (activity === "docs") {
    return (
      <g className={styles.thoughtDocs}>
        <rect x="53" y="12" width="16" height="20" rx="2" />
        <path d="M57 18h8M57 22h8M57 26h5" />
      </g>
    );
  }
  if (activity === "image") {
    return (
      <g className={styles.thoughtImage}>
        <rect x="50" y="13" width="22" height="18" rx="2" />
        <path d="M52 29l6-7 4 4 3-3 5 6" />
        <circle cx="66" cy="18" r="2.2" />
      </g>
    );
  }
  return (
    <g className={styles.thoughtDots}>
      <circle cx="51" cy="24" r="3.2" />
      <circle cx="61" cy="24" r="3.2" />
      <circle cx="71" cy="24" r="3.2" />
    </g>
  );
}

/** The thought bubble, the sound waves and the sparkles around a figure. They sit
 *  outside the artwork, so Issa and Maia share them. */
function FigureEffects({ state, activity }: { state: IssaAvatarState; activity: IssaActivity }) {
  return (
    <>
      <svg className={styles.figureThought} data-show={state === "thinking" || undefined} viewBox="0 0 96 64" aria-hidden="true">
        <circle className={styles.thoughtTrail} cx="9" cy="57" r="4" />
        <circle className={styles.thoughtTrail} cx="21" cy="44" r="6" />
        <path className={styles.thoughtCloud} d="M38 36A9 9 0 0 1 40 18A12 12 0 0 1 60 10A12 12 0 0 1 80 18A10 10 0 0 1 84 36A40 40 0 0 1 38 36Z" />
        <ThoughtIcon activity={activity} />
      </svg>
      <svg className={styles.figureTalk} data-show={state === "responding" || undefined} viewBox="0 0 20 32" aria-hidden="true">
        <path d="M3 11Q7 16 3 21" />
        <path d="M8 7Q14 16 8 25" />
        <path d="M13 3Q21 16 13 29" />
      </svg>
      {state === "done" && (
        <svg className={styles.figureSparkles} viewBox="0 0 100 100" aria-hidden="true">
          <path d={sparkle(16, 20, 7)} />
          <path d={sparkle(84, 12, 9)} />
          <path d={sparkle(92, 50, 6)} />
          <path d={sparkle(8, 56, 5)} />
        </svg>
      )}
    </>
  );
}

/** The assistant's own animated figure, so a Maikoz operator never sees Issa's.
 *  With effects on, Maia brings the thought bubble, sound waves and sparkles;
 *  Issa's figure stays exactly as it was designed. */
export function AssistantFigure({ state, activity = null, effects = false }: {
  state: IssaAvatarState;
  activity?: IssaActivity;
  effects?: boolean;
}) {
  const assistant = useAssistant();
  return (
    <>
      {assistant.figure === "maia" ? <MaiaAnimatedAvatar state={state} /> : <IssaAnimatedAvatar state={state} />}
      {effects && assistant.figure === "maia" && <FigureEffects state={state} activity={activity} />}
    </>
  );
}

/** The welcome screen's figure. Maia greets with a nod, a hop and sparkles, then
 *  settles; Issa keeps her round portrait there. */
export function AssistantWelcome({ size }: { size: number }) {
  const assistant = useAssistant();
  const [state, setState] = useState<IssaAvatarState>("done");

  useEffect(() => {
    const timer = window.setTimeout(() => setState("idle"), 1600);
    return () => window.clearTimeout(timer);
  }, []);

  if (assistant.figure !== "maia") {
    return <AssistantAvatar size={size} ring style={{ marginBottom: "var(--space-3)" }} />;
  }
  return (
    <span className={styles.welcomeFigure} style={{ width: size }}>
      <MaiaAnimatedAvatar state={state} />
      <FigureEffects state={state} activity={null} />
    </span>
  );
}

interface IssaChatAvatarProps {
  /** false keeps the column empty so the row still lines up. */
  show: boolean;
  /** Animation state; an answer row defaults to one nod as it appears. */
  state?: IssaAvatarState;
  /** Pre-computed model class from avatarModelClass; omit for the default tint. */
  modelClass?: string;
  /** What she is busy with, for the thought bubble. */
  activity?: IssaActivity;
  /** Bubble, sound waves and sparkles: for the live row, not for history. */
  effects?: boolean;
}

/**
 * Issa's column beside an answer, used by the quick chat. The Issa page leaves
 * the column out entirely (answers start at the message column) and shows the
 * figure in the dock above its input instead.
 */
export function IssaChatAvatar({ show, state = "done", modelClass = "", activity = null, effects = false }: IssaChatAvatarProps) {
  if (!show) return <span className={styles.avatarSlot} aria-hidden="true" />;
  const classes = [styles.avatarSlot, styles.avatarSlotShown, modelClass].filter(Boolean).join(" ");
  return (
    <span className={classes}>
      <AssistantFigure state={state} activity={activity} effects={effects} />
    </span>
  );
}
